import * as Dialog from "@radix-ui/react-dialog";
import { Label } from "@radix-ui/react-label";
import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { ButtonRow, FormRow } from "../../components/dialog";
import { LoadingMessagePanel } from "../../components/loadingPanel";
import { Settings } from "../../domain/settings";
import { AdminService } from "./adminService";

const toLocalInput = (epochMs?: number) =>
  epochMs ? DateTime.fromMillis(epochMs).toFormat("yyyy-MM-dd'T'HH:mm") : "";

export const SettingsDialog = ({
  adminService,
  onClose,
}: {
  adminService: Pick<AdminService, "getSettings" | "saveSettings">;
  onClose: () => void;
}) => {
  const [settings, setSettings] = useState<Settings | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const loaded = await adminService.getSettings();
        if (loaded.status == "OK" && loaded.value) {
          setSettings(loaded.value);
        } else {
          setFailed(true);
        }
      } catch (e) {
        console.log("Unable to load settings", e);
        setFailed(true);
      }
    })();
  }, []);

  const save = async () => {
    if (!settings) {
      return;
    }
    try {
      await adminService.saveSettings(settings);
      onClose();
    } catch (e) {
      console.log("Unable to save settings", e);
      setFailed(true);
    }
  };

  const form = (settings: Settings) => (
    <>
      <FormRow>
        <Label className="formLabel" htmlFor={"concert-end-time"}>
          Concert end time
        </Label>
        <input
          id={"concert-end-time"}
          type={"datetime-local"}
          value={toLocalInput(settings.concertEndTime)}
          onChange={(e) =>
            setSettings({
              ...settings,
              concertEndTime: e.currentTarget.value
                ? new Date(e.currentTarget.value).getTime()
                : undefined,
            })
          }
        />
      </FormRow>
      <FormRow>
        <Label className="formLabel" htmlFor={"buffer-minutes"}>
          End-of-concert buffer (minutes)
        </Label>
        <input
          id={"buffer-minutes"}
          type={"number"}
          min={0}
          step={1}
          value={settings.bufferMinutes}
          onChange={(e) =>
            setSettings({
              ...settings,
              bufferMinutes: e.currentTarget.valueAsNumber,
            })
          }
        />
      </FormRow>
      <FormRow>
        <Label className="formLabel" htmlFor={"song-length-minutes"}>
          Song length (minutes)
        </Label>
        <input
          id={"song-length-minutes"}
          type={"number"}
          min={0.5}
          step={0.1}
          value={settings.songLengthMinutes}
          onChange={(e) =>
            setSettings({
              ...settings,
              songLengthMinutes: e.currentTarget.valueAsNumber,
            })
          }
        />
      </FormRow>
      <FormRow>
        <Label className="formLabel" htmlFor={"show-time-divider"}>
          Show time divider on kiosk
        </Label>
        <input
          id={"show-time-divider"}
          type={"checkbox"}
          checked={settings.showTimeDivider}
          onChange={(e) =>
            setSettings({
              ...settings,
              showTimeDivider: e.currentTarget.checked,
            })
          }
        />
      </FormRow>
      <ButtonRow>
        <button
          onClick={save}
          disabled={
            !(settings.songLengthMinutes > 0 && settings.bufferMinutes >= 0)
          }
        >
          Save
        </button>
        <button onClick={onClose}>Cancel</button>
      </ButtonRow>
    </>
  );

  return (
    <Dialog.Root modal={true} open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay">
          <Dialog.Content
            className="DialogContent"
            onEscapeKeyDown={onClose}
            onPointerDownOutside={onClose}
          >
            <Dialog.DialogTitle>Settings</Dialog.DialogTitle>
            <span role={"alert"} className={"ErrorAlert"} hidden={!failed}>
              Something went wrong. Please try again.
            </span>
            {settings ? (
              form(settings)
            ) : failed ? null : (
              <LoadingMessagePanel />
            )}
            {!settings && failed && (
              <ButtonRow>
                <button onClick={onClose}>Close</button>
              </ButtonRow>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
